import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Plus, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { api, shortDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function iso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function parseISO(s) {
  const [y, m, d] = s.split("T")[0].split("-").map(Number);
  return new Date(y, m - 1, d);
}

export default function Calendar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cnats, setCnats] = useState([]);
  const [viewUser, setViewUser] = useState(user.id);
  const [data, setData] = useState({ events: [], is_owner: true });
  const [blockTypes, setBlockTypes] = useState([]);
  const [cursor, setCursor] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [open, setOpen] = useState(false);
  const [presetDate, setPresetDate] = useState("");

  const load = () => api.get("/calendar", { params: { user_id: viewUser } }).then((r) => setData(r.data));
  useEffect(() => {
    api.get("/users", { params: { role: "cnat" } }).then((r) => setCnats(r.data));
    api.get("/block-types").then((r) => setBlockTypes(r.data));
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [viewUser]);

  const del = async (id) => { await api.delete(`/calendar/blocks/${id}`); toast.success("Removed"); load(); };

  const eventsByDate = useMemo(() => {
    const map = {};
    for (const ev of data.events) {
      if (!ev.start) continue;
      const start = parseISO(ev.start);
      const end = ev.end ? parseISO(ev.end) : start;
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const k = iso(d);
        (map[k] = map[k] || []).push(ev);
      }
    }
    return map;
  }, [data.events]);

  const cells = useMemo(() => {
    const year = cursor.getFullYear(), month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const offset = (first.getDay() + 6) % 7; // Monday-first
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < offset; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(new Date(year, month, d));
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [cursor]);

  const todayIso = iso(new Date());
  const shift = (n) => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + n, 1));
  const openDay = (dateStr) => { if (!data.is_owner) return; setPresetDate(dateStr); setOpen(true); };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Calendar / ROTA</h1>
          <p className="mt-1 text-sm text-slate-500">{data.is_owner ? "Your scheduled work and blocked time." : "Viewing a colleague's ROTA (read-only)."}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={viewUser} onValueChange={setViewUser}>
            <SelectTrigger data-testid="calendar-user-select" className="w-52"><SelectValue placeholder="View calendar" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={user.id}>My calendar</SelectItem>
              {cnats.filter((c) => c.id !== user.id).map((c) => <SelectItem key={c.id} value={c.id}>{c.name} (CNAT)</SelectItem>)}
            </SelectContent>
          </Select>
          {data.is_owner && (
            <Button data-testid="add-block-button" onClick={() => { setPresetDate(""); setOpen(true); }} className="bg-orange-500 hover:bg-orange-600">
              <Plus className="mr-1.5 h-4 w-4" /> Block time
            </Button>
          )}
        </div>
      </div>

      {/* Month grid */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 data-testid="calendar-month-label" className="font-heading text-lg font-bold text-slate-900">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</h2>
          <div className="flex items-center gap-1">
            <button data-testid="cal-prev" onClick={() => shift(-1)} className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /></button>
            <button data-testid="cal-today" onClick={() => { const n = new Date(); setCursor(new Date(n.getFullYear(), n.getMonth(), 1)); }} className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">Today</button>
            <button data-testid="cal-next" onClick={() => shift(1)} className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:bg-slate-50"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-px border-b border-slate-100 pb-2 text-center">
          {WEEKDAYS.map((w) => <div key={w} className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{w}</div>)}
        </div>

        <div className="mt-1 grid grid-cols-7 gap-1">
          {cells.map((date, i) => {
            if (!date) return <div key={i} className="min-h-[92px] rounded-lg bg-slate-50/50" />;
            const key = iso(date);
            const evs = eventsByDate[key] || [];
            const isToday = key === todayIso;
            return (
              <div
                key={i}
                data-testid="calendar-day"
                onClick={() => openDay(key)}
                className={`min-h-[92px] rounded-lg border p-1.5 transition-colors ${isToday ? "border-orange-300 bg-orange-50/40" : "border-slate-100 hover:bg-slate-50"} ${data.is_owner ? "cursor-pointer" : ""}`}
              >
                <div className="mb-1 flex items-center justify-between">
                  <span className={`text-xs font-semibold ${isToday ? "flex h-5 w-5 items-center justify-center rounded-full bg-orange-500 text-white" : "text-slate-500"}`}>{date.getDate()}</span>
                </div>
                <div className="space-y-1">
                  {evs.slice(0, 3).map((ev, j) => (
                    <div
                      key={j}
                      data-testid="calendar-event"
                      onClick={(e) => { e.stopPropagation(); if (ev.kind === "work" && ev.employee_name) return; }}
                      title={`${ev.title}${ev.employee_name ? " — " + ev.employee_name : ""}`}
                      className={`truncate rounded px-1.5 py-0.5 text-[10px] font-medium ${ev.kind === "work" ? "bg-orange-100 text-orange-700" : "bg-slate-800 text-white"}`}
                    >
                      {ev.kind === "work" ? ev.title : ev.block_type}
                    </div>
                  ))}
                  {evs.length > 3 && <div className="px-1 text-[10px] font-medium text-slate-400">+{evs.length - 3} more</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend + upcoming */}
      <div className="flex items-center gap-4 text-xs text-slate-500">
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-orange-100 ring-1 ring-orange-300" /> Scheduled work</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-slate-800" /> Blocked time</span>
        {data.is_owner && <span className="text-slate-400">Tip: click a day to block time.</span>}
      </div>

      {data.events.filter((e) => e.kind === "block").length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="mb-3 font-heading text-sm font-bold text-slate-900">Your blocked time</h3>
          <ul className="space-y-2">
            {data.events.filter((e) => e.kind === "block").sort((a, b) => (a.start || "").localeCompare(b.start || "")).map((ev) => (
              <li key={ev.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{ev.block_type}</span>
                  <span className="text-slate-700">{ev.title}</span>
                  <span className="text-xs text-slate-400">{shortDate(ev.start)}{ev.end && ev.end !== ev.start ? ` – ${shortDate(ev.end)}` : ""}</span>
                </div>
                {data.is_owner && <button onClick={() => del(ev.id)} className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <BlockDialog open={open} onOpenChange={setOpen} blockTypes={blockTypes} presetDate={presetDate} onSaved={load} />
    </div>
  );
}

function BlockDialog({ open, onOpenChange, blockTypes, presetDate, onSaved }) {
  const [form, setForm] = useState({ title: "", block_type: "", start: "", end: "", notes: "" });
  useEffect(() => { if (open) setForm({ title: "", block_type: blockTypes[0] || "", start: presetDate || "", end: presetDate || "", notes: "" }); }, [open, blockTypes, presetDate]);

  const save = async () => {
    if (!form.block_type || !form.start) { toast.error("Type and start date required"); return; }
    await api.post("/calendar/blocks", { ...form, title: form.title || form.block_type, all_day: true, end: form.end || form.start });
    toast.success("Time blocked");
    onOpenChange(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="block-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Block out time</DialogTitle>
          <DialogDescription>Reserve slots for other work.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase text-slate-500">Type</Label>
            <Select value={form.block_type} onValueChange={(v) => setForm((f) => ({ ...f, block_type: v }))}>
              <SelectTrigger className="mt-1.5" data-testid="block-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>{blockTypes.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Title (optional)</Label>
            <Input className="mt-1.5" data-testid="block-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase text-slate-500">Start</Label>
              <Input className="mt-1.5" type="date" data-testid="block-start-input" value={form.start} onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">End</Label>
              <Input className="mt-1.5" type="date" data-testid="block-end-input" value={form.end} onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))} />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button data-testid="block-save-button" onClick={save} className="bg-orange-500 hover:bg-orange-600">Block time</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
